/**
 * ============================================================================
 * TaskFlow ERP
 * Module : Campaign Management
 * Feature: Prospect Import
 *
 * File:
 * src/modules/campaign/prospectImport/constants/campaignImportConstants.js
 *
 * Description:
 * Centralized constants for Prospect Import feature.
 *
 * This file contains only constants used across the
 * Prospect Import pages, components and services.
 *
 * No business logic should be implemented here.
 * ============================================================================
 */

/**
 * ============================================================================
 * Module Information
 * ============================================================================
 */

export const MODULE_NAME = "Campaign Management";
export const FEATURE_NAME = "Prospect Import";

/**
 * ============================================================================
 * Accepted File Types
 * ============================================================================
 */

export const ACCEPTED_FILE_TYPES = [
    ".xlsx",
    ".xls",
    ".csv"
];

export const ACCEPTED_MIME_TYPES = [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
    "text/csv"
];

/**
 * ============================================================================
 * Upload Configuration
 * ============================================================================
 */

export const MAX_FILE_SIZE_MB = 10;

export const MAX_FILE_SIZE_BYTES =
    MAX_FILE_SIZE_MB * 1024 * 1024;

/**
 * ============================================================================
 * Import Status
 * ============================================================================
 */

export const IMPORT_STATUS = {
    VERIFIED: "Verified",
    WARNING: "Warning",
    DUPLICATE: "Duplicate",
    INVALID: "Invalid"
};

/**
 * ============================================================================
 * Status Badge Variant
 * (React Bootstrap Badge)
 * ============================================================================
 */

export const STATUS_VARIANTS = {
    Verified: "success",
    Warning: "warning",
    Duplicate: "primary",
    Invalid: "danger"
};

/**
 * ============================================================================
 * Grid Columns
 * ============================================================================
 */

export const IMPORT_GRID_COLUMNS = [
    {
        key: "select",
        label: "",
        width: 60
    },
    {
        key: "row_no",
        label: "Row No",
        width: 90
    },
    {
        key: "name",
        label: "Prospect Name",
        width: 260
    },
    {
        key: "contacts",
        label: "Contacts",
        width: 120
    },
    {
        key: "external_id",
        label: "External ID",
        width: 180
    },
    {
        key: "status",
        label: "Status",
        width: 140
    },
    {
        key: "remarks",
        label: "Remarks",
        width: 350
    }
];

/**
 * ============================================================================
 * Summary Cards
 * ============================================================================
 */

export const SUMMARY_CARDS = [
    {
        key: "total_rows",
        label: "Total Rows"
    },
    {
        key: "valid_rows",
        label: "Valid Rows"
    },
    {
        key: "invalid_rows",
        label: "Invalid Rows"
    },
    {
        key: "duplicate_rows",
        label: "Duplicate Rows"
    },
    {
        key: "warning_rows",
        label: "Warning Rows"
    }
];

/**
 * ============================================================================
 * Button Labels
 * ============================================================================
 */

export const BUTTON_LABELS = {
    DOWNLOAD_TEMPLATE: "Download Template",
    CHOOSE_FILE: "Choose File",
    VERIFY: "Verify File",
    IMPORT: "Import",
    CANCEL: "Cancel",
    COMPARE: "Compare",
    EDIT: "Edit",
    VIEW: "View",
    CLOSE: "Close"
};

/**
 * ============================================================================
 * Validation Messages
 * ============================================================================
 */

export const VALIDATION_MESSAGES = {
    FILE_REQUIRED: "Please select a file.",
    INVALID_FILE_TYPE: "Only Excel (.xlsx, .xls) and CSV files are allowed.",
    FILE_TOO_LARGE: `Maximum allowed file size is ${MAX_FILE_SIZE_MB} MB.`,
    VERIFY_SUCCESS: "File verified successfully.",
    IMPORT_SUCCESS: "Prospects imported successfully.",
    NO_RECORD_SELECTED: "Please select a record."
};

/**
 * ============================================================================
 * Search Placeholder
 * ============================================================================
 */

export const SEARCH_PLACEHOLDER =
    "Search Prospect...";

/**
 * ============================================================================
 * Filter Options
 * ============================================================================
 */

export const STATUS_FILTER_OPTIONS = [
    {
        label: "All",
        value: "ALL"
    },
    {
        label: "Verified",
        value: IMPORT_STATUS.VERIFIED
    },
    {
        label: "Warning",
        value: IMPORT_STATUS.WARNING
    },
    {
        label: "Duplicate",
        value: IMPORT_STATUS.DUPLICATE
    },
    {
        label: "Invalid",
        value: IMPORT_STATUS.INVALID
    }
];

/**
 * ============================================================================
 * Default Values
 * ============================================================================
 */

export const DEFAULT_SUMMARY = {
    total_rows: 0,
    valid_rows: 0,
    invalid_rows: 0,
    duplicate_rows: 0,
    warning_rows: 0
};

export const DEFAULT_PROGRESS = 0;

export const DEFAULT_PAGE_SIZE = 10;

/**
 * ============================================================================
 * Progress Messages
 * ============================================================================
 */

export const PROGRESS_MESSAGES = {
    UPLOADING: "Uploading file...",
    READING: "Reading spreadsheet...",
    VERIFYING: "Verifying records...",
    VALIDATING: "Validating data...",
    COMPLETED: "Completed"
};